const IS_MOBILE = typeof navigator !== 'undefined'
    && (/Mobi|Android/i.test(navigator.userAgent)
        || (typeof window !== 'undefined' && window.matchMedia
            && window.matchMedia('(max-width: 768px)').matches));

const SFX_KEY = 'slimespins_sfx';
const MUSIC_KEY = 'slimespins_music_v2';

export const MUSIC_CEILING = 0.15;
export const DEFAULT_MUSIC = 1;
export const DEFAULT_SFX = 0.7;

const MAX_VOICES = IS_MOBILE ? 32 : 72;
const MAX_COIN_TICKS = IS_MOBILE ? 5 : 12;
const IDLE_SUSPEND_MS = 6000;

const clamp01 = v => Math.min(1, Math.max(0, v));

const readVol = (key, fallback) => {
    try {
        const raw = parseFloat(localStorage.getItem(key));
        return Number.isFinite(raw) ? clamp01(raw) : fallback;
    } catch (e) {
        return fallback;
    }
};

const storeVol = (key, v) => {
    try { localStorage.setItem(key, String(v)); } catch (e) { return; }
};

let sfxVol = readVol(SFX_KEY, DEFAULT_SFX);
let musicVol = readVol(MUSIC_KEY, DEFAULT_MUSIC);

let ctx = null;
let master = null;
let sfxBus = null;
let musicBus = null;
let waves = null;
let noiseBuf = null;
let pending = [];
let idleTimer = null;
let gestureBound = false;

let musicEl = null;
let musicSrcNode = null;
let musicRouted = false;
let musicWanted = false;
let musicPlay = null;
let fadeRaf = null;

let reelLoop = null;

const pulseWave = (audio, duty) => {
    const n = 28;
    const real = new Float32Array(n);
    const imag = new Float32Array(n);
    for (let i = 1; i < n; i++) imag[i] = (2 / (i * Math.PI)) * Math.sin(Math.PI * i * duty);
    return audio.createPeriodicWave(real, imag, { disableNormalization: false });
};

const buildNoise = (audio) => {
    const len = Math.floor(audio.sampleRate * 0.7);
    const buf = audio.createBuffer(1, len, audio.sampleRate);
    const data = buf.getChannelData(0);
    let seed = 0x2f6e2b1;
    for (let i = 0; i < len; i++) {
        seed = (seed * 1103515245 + 12345) & 0x7fffffff;
        data[i] = (seed / 0x3fffffff) - 1;
    }
    return buf;
};

function ensureCtx() {
    if (ctx) return ctx;
    if (typeof window === 'undefined') return null;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;

    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = 1;
    master.connect(ctx.destination);

    sfxBus = ctx.createGain();
    sfxBus.gain.value = sfxVol;
    sfxBus.connect(master);

    musicBus = ctx.createGain();
    musicBus.gain.value = musicVol * MUSIC_CEILING;
    musicBus.connect(master);

    waves = {
        pulse12: pulseWave(ctx, 0.125),
        pulse25: pulseWave(ctx, 0.25),
        pulse50: pulseWave(ctx, 0.5),
    };
    noiseBuf = buildNoise(ctx);

    routeMusic();
    bindGesture();
    return ctx;
}

function bindGesture() {
    if (gestureBound || typeof document === 'undefined') return;
    gestureBound = true;
    const wake = () => {
        if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
        playMusicEl();
    };
    ['pointerdown', 'keydown', 'touchstart'].forEach(e =>
        document.addEventListener(e, wake, { passive: true })
    );
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            if (musicEl && !musicEl.paused) musicEl.pause();
            if (ctx && ctx.state === 'running') ctx.suspend().catch(() => {});
        } else {
            if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
            playMusicEl();
        }
    });
}

function budget(audio, endsAt) {
    const t = audio.currentTime;
    if (pending.length) pending = pending.filter(e => e > t);
    if (pending.length >= MAX_VOICES) return false;
    pending.push(endsAt);
    return true;
}

function touchIdle() {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
        idleTimer = null;
        if (!ctx || ctx.state !== 'running') return;
        if (pending.length || reelLoop) return;
        if (musicEl && !musicEl.paused && musicVol > 0) return;
        ctx.suspend().catch(() => {});
    }, IDLE_SUSPEND_MS);
}

function live() {
    if (sfxVol <= 0) return null;
    const audio = ensureCtx();
    if (!audio) return null;
    if (audio.state === 'suspended') audio.resume().catch(() => {});
    return audio;
}

function waveFor(kind) {
    if (kind === 'p12') return waves.pulse12;
    if (kind === 'p25') return waves.pulse25;
    if (kind === 'p50') return waves.pulse50;
    return null;
}

function tone(opts) {
    const audio = live();
    if (!audio) return;

    const {
        freq, to, dur = 0.12, vol = 0.3, kind = 'p50',
        at = 0, attack = 0.004, hold = 0.25, detune = 0,
    } = opts;

    const t = audio.currentTime + at;
    if (!budget(audio, t + dur + 0.02)) return;
    const osc = audio.createOscillator();
    const gain = audio.createGain();

    const w = waveFor(kind);
    if (w) osc.setPeriodicWave(w); else osc.type = kind;
    if (detune) osc.detune.value = detune;

    osc.frequency.setValueAtTime(freq, t);
    if (to && to !== freq) osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), t + dur);

    const peak = Math.max(0.0005, vol);
    gain.gain.setValueAtTime(0.0005, t);
    gain.gain.linearRampToValueAtTime(peak, t + attack);
    gain.gain.setValueAtTime(peak, t + Math.max(attack, dur * hold));
    gain.gain.exponentialRampToValueAtTime(0.0005, t + dur);

    osc.connect(gain);
    gain.connect(sfxBus);

    osc.onended = () => { gain.disconnect(); };
    osc.start(t);
    osc.stop(t + dur + 0.02);
    touchIdle();
}

function noise(opts) {
    const audio = live();
    if (!audio) return;

    const {
        dur = 0.1, vol = 0.2, at = 0,
        cutoff = 2200, cutoffTo = 0, type = 'lowpass', q = 1,
    } = opts;

    const t = audio.currentTime + at;
    if (!budget(audio, t + dur + 0.02)) return;
    const src = audio.createBufferSource();
    src.buffer = noiseBuf;
    src.loop = true;

    const filter = audio.createBiquadFilter();
    filter.type = type;
    filter.Q.value = q;
    filter.frequency.setValueAtTime(cutoff, t);
    if (cutoffTo) filter.frequency.exponentialRampToValueAtTime(Math.max(60, cutoffTo), t + dur);

    const gain = audio.createGain();
    const peak = Math.max(0.0005, vol);
    gain.gain.setValueAtTime(0.0005, t);
    gain.gain.linearRampToValueAtTime(peak, t + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0005, t + dur);

    src.connect(filter);
    filter.connect(gain);
    gain.connect(sfxBus);

    src.onended = () => { filter.disconnect(); gain.disconnect(); };
    src.start(t);
    src.stop(t + dur + 0.02);
    touchIdle();
}

const midi = n => 440 * Math.pow(2, (n - 69) / 12);

function sequence(notes, opts) {
    const { gap = 0.075, dur = 0.12, vol = 0.28, kind = 'p25', at = 0 } = opts || {};
    notes.forEach((n, i) => {
        if (n === null) return;
        tone({ freq: midi(n), dur, vol, kind, at: at + i * gap });
    });
}

export function primeAudio() {
    const audio = ensureCtx();
    if (audio && audio.state === 'suspended') audio.resume().catch(() => {});
}

export function playUi() {
    tone({ freq: 720, to: 980, dur: 0.05, vol: 0.16, kind: 'p12' });
}

export function playDeny() {
    tone({ freq: 200, to: 120, dur: 0.16, vol: 0.2, kind: 'p50' });
}

export function playSpinStart() {
    tone({ freq: 520, to: 260, dur: 0.14, vol: 0.2, kind: 'p25' });
    tone({ freq: 780, to: 390, dur: 0.14, vol: 0.1, kind: 'p12', at: 0.02 });
    noise({ dur: 0.22, vol: 0.12, cutoff: 900, cutoffTo: 3600 });
}

export function playReelStop(index) {
    const i = Math.max(0, index || 0);
    tone({ freq: 190 - i * 9, to: 96 - i * 4, dur: 0.09, vol: 0.26, kind: 'p50' });
    if (!IS_MOBILE) noise({ dur: 0.06, vol: 0.14, cutoff: 3200, cutoffTo: 700 });
}

export function playTease() {
    sequence([69, 74, 78, 81], { gap: 0.085, dur: 0.1, vol: 0.16, kind: 'p12' });
    noise({ dur: 0.4, vol: 0.07, cutoff: 500, cutoffTo: 4200, type: 'bandpass', q: 3 });
}

const WIN_LINES = [
    [72, 76, 79],
    [72, 76, 79, 84],
    [72, 76, 79, 84, 81, 84, 88],
    [72, 79, 76, 84, 81, 88, 84, 91, 96],
];

export function playWin(tier) {
    const t = Math.min(WIN_LINES.length - 1, Math.max(0, tier || 0));
    const notes = WIN_LINES[t];
    sequence(notes, { gap: 0.072, dur: 0.13, vol: 0.2 + t * 0.03, kind: 'p25' });
    if (t >= 2) {
        sequence(notes.map(n => n - 24), { gap: 0.072, dur: 0.16, vol: 0.16, kind: 'triangle' });
    }
    if (t >= 3 && !IS_MOBILE) {
        noise({ dur: 0.5, vol: 0.08, cutoff: 900, cutoffTo: 5200, type: 'bandpass', q: 2 });
    }
}

export function playCoins(count) {
    const n = Math.min(MAX_COIN_TICKS, Math.max(1, count || 1));
    for (let i = 0; i < n; i++) {
        tone({ freq: midi(93 + (i % 2) * 5), dur: 0.05, vol: 0.13, kind: 'p12', at: i * 0.055 });
    }
}

export function playTrigger(kind) {
    if (kind === 'jackpot') { playJackpot(); return; }
    if (kind === 'wheel') {
        sequence([76, 83, 88, 95], { gap: 0.09, dur: 0.16, vol: 0.22, kind: 'p12' });
        tone({ freq: 300, to: 1500, dur: 0.5, vol: 0.1, kind: 'triangle', at: 0.1 });
        return;
    }
    sequence([70, 75, 82, 87], { gap: 0.08, dur: 0.14, vol: 0.22, kind: 'p25' });
    if (!IS_MOBILE) noise({ dur: 0.45, vol: 0.09, cutoff: 1400, cutoffTo: 6000, type: 'bandpass', q: 3 });
}

export function playJackpot() {
    sequence([60, 64, 67, 72, 76, 79, 84], { gap: 0.085, dur: 0.15, vol: 0.26, kind: 'p25' });
    sequence([48, 52, 55, 60], { gap: 0.17, dur: 0.3, vol: 0.18, kind: 'triangle' });
    [84, 88, 91, 96].forEach((n, i) => {
        tone({ freq: midi(n), dur: 0.9, vol: 0.14, kind: 'p12', at: 0.62 + i * 0.02, hold: 0.6 });
    });
    if (!IS_MOBILE) noise({ dur: 0.8, vol: 0.09, cutoff: 800, cutoffTo: 6500, type: 'bandpass', q: 2, at: 0.6 });
}

export function playBonusIntro(isSuper) {
    const line = isSuper
        ? [69, 72, 75, 78, 81, 78, 75, 84]
        : [69, 72, 76, 81, 76, 81, 84];
    sequence(line, { gap: 0.11, dur: 0.18, vol: 0.24, kind: 'p25' });
    sequence(line.map(n => n - 24), { gap: 0.11, dur: 0.22, vol: 0.17, kind: 'triangle' });
    if (isSuper) tone({ freq: 60, to: 240, dur: 1.1, vol: 0.12, kind: 'sawtooth', hold: 0.8 });
}

export function playRetrigger() {
    sequence([81, 86, 93], { gap: 0.07, dur: 0.13, vol: 0.24, kind: 'p12' });
}

export function playWheelLand(count) {
    if (count >= 2) {
        sequence([76, 81, 88, 93], { gap: 0.075, dur: 0.16, vol: 0.26, kind: 'p12' });
        sequence([52, 57, 64], { gap: 0.11, dur: 0.26, vol: 0.18, kind: 'triangle' });
        tone({ freq: 400, to: 1900, dur: 0.55, vol: 0.12, kind: 'triangle', at: 0.06 });
        if (!IS_MOBILE) noise({ dur: 0.55, vol: 0.09, cutoff: 1200, cutoffTo: 6800, type: 'bandpass', q: 3, at: 0.05 });
        return;
    }
    tone({ freq: midi(76), to: midi(83), dur: 0.26, vol: 0.2, kind: 'p25' });
    tone({ freq: midi(64), dur: 0.3, vol: 0.12, kind: 'triangle', at: 0.04 });
}

const WOMP = {
    vol: 0.12,
    speed: 1,
    notes: [233.08, 220.00, 207.65, 196.00],
    step: 0.17,
    tail: 0.34,
    droopTo: 150,
    formantOpen: 1700,
    formantShut: 620,
    boost: 9,
};

export function playWomp() {
    const audio = live();
    if (!audio) return;

    const { notes, droopTo, formantOpen, formantShut, boost, vol: V } = WOMP;
    const step = WOMP.step * WOMP.speed;
    const tail = WOMP.tail * WOMP.speed;
    const t = audio.currentTime;
    const endAt = t + step * (notes.length - 1) + tail;
    if (!budget(audio, endAt + 0.08)) return;

    const QUIET = 0.0008;
    const GAP = QUIET * 12;

    const body = audio.createBiquadFilter();
    body.type = 'lowpass';
    body.frequency.value = 2600;
    body.Q.value = 0.7;

    const wah = audio.createBiquadFilter();
    wah.type = 'peaking';
    wah.Q.value = 2.6;
    wah.gain.value = boost;
    wah.frequency.setValueAtTime(formantOpen, t);

    const gain = audio.createGain();
    gain.gain.setValueAtTime(QUIET, t);

    notes.forEach((hz, i) => {
        const last = i === notes.length - 1;
        const on = t + step * i;
        const len = last ? tail : step;

        gain.gain.exponentialRampToValueAtTime(V, on + 0.014);
        gain.gain.setValueAtTime(V, on + len * 0.55);
        gain.gain.exponentialRampToValueAtTime(last ? QUIET : GAP, on + len * (last ? 1 : 0.97));

        wah.frequency.exponentialRampToValueAtTime(formantShut, on + len * 0.7);
        if (!last) wah.frequency.exponentialRampToValueAtTime(formantOpen, on + len);
    });
    wah.frequency.exponentialRampToValueAtTime(400, endAt);

    body.connect(wah);
    wah.connect(gain);
    gain.connect(sfxBus);

    const detunes = IS_MOBILE ? [0] : [0, -8];
    let remaining = detunes.length;
    detunes.forEach((cents) => {
        const osc = audio.createOscillator();
        osc.type = 'sawtooth';
        osc.detune.value = cents;
        const f = osc.frequency;
        f.setValueAtTime(notes[0], t);
        notes.forEach((hz, i) => {
            if (i === 0) return;
            const on = t + step * i;
            f.setValueAtTime(notes[i - 1], on);
            f.exponentialRampToValueAtTime(hz, on + step * 0.28);
        });
        f.setValueAtTime(notes[notes.length - 1], t + step * (notes.length - 1) + tail * 0.35);
        f.exponentialRampToValueAtTime(droopTo, endAt);
        osc.connect(body);
        osc.onended = () => {
            remaining -= 1;
            if (remaining <= 0) { body.disconnect(); wah.disconnect(); gain.disconnect(); }
        };
        osc.start(t);
        osc.stop(endAt + 0.05);
    });
}

export function playWheelSpin() {
    tone({ freq: 180, to: 720, dur: 0.7, vol: 0.14, kind: 'triangle' });
    noise({ dur: 0.7, vol: 0.09, cutoff: 600, cutoffTo: 4000, type: 'bandpass', q: 2 });
}

export function playWheelTick() {
    tone({ freq: 1500, to: 900, dur: 0.028, vol: 0.11, kind: 'p12' });
}

export function playPrize() {
    sequence([76, 81, 84, 88], { gap: 0.09, dur: 0.2, vol: 0.24, kind: 'p25' });
    sequence([52, 57, 60], { gap: 0.12, dur: 0.28, vol: 0.15, kind: 'triangle' });
}

export function playLose() {
    tone({ freq: midi(60), to: midi(56), dur: 0.22, vol: 0.12, kind: 'p50' });
}

export function startReelLoop() {
    if (sfxVol <= 0 || reelLoop) return;
    const audio = live();
    if (!audio) return;

    const src = audio.createBufferSource();
    src.buffer = noiseBuf;
    src.loop = true;

    const filter = audio.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 6;
    filter.frequency.value = IS_MOBILE ? 950 : 1150;

    const gain = audio.createGain();
    gain.gain.setValueAtTime(0.0005, audio.currentTime);
    gain.gain.linearRampToValueAtTime(0.05, audio.currentTime + 0.08);

    src.connect(filter);
    filter.connect(gain);
    gain.connect(sfxBus);
    src.start();

    reelLoop = { src, filter, gain };
}

export function stopReelLoop() {
    if (!reelLoop || !ctx) return;
    const { src, filter, gain } = reelLoop;
    reelLoop = null;
    const t = ctx.currentTime;
    try {
        gain.gain.cancelScheduledValues(t);
        gain.gain.setValueAtTime(Math.max(0.0005, gain.gain.value), t);
        gain.gain.exponentialRampToValueAtTime(0.0005, t + 0.12);
        src.stop(t + 0.15);
    } catch (e) { gain.disconnect(); }
    src.onended = () => { filter.disconnect(); gain.disconnect(); };
    touchIdle();
}

function playMusicEl() {
    if (!musicEl || !musicWanted || musicVol <= 0) return;
    if (!musicEl.paused) return;
    const p = musicEl.play();
    musicPlay = (p && typeof p.then === 'function') ? p : null;
    if (musicPlay) musicPlay.catch(() => {});
}

function haltMusicEl() {
    if (!musicEl) return;
    const el = musicEl;
    el.pause();
    el.currentTime = 0;
    if (musicPlay) {
        musicPlay.then(() => {
            if (!musicWanted) { el.pause(); el.currentTime = 0; }
        }).catch(() => {});
        musicPlay = null;
    }
}

function routeMusic() {
    if (!ctx || !musicEl || musicRouted) return;
    try {
        musicSrcNode = ctx.createMediaElementSource(musicEl);
        musicSrcNode.connect(musicBus);
        musicRouted = true;
        musicEl.volume = 1;
    } catch (e) {
        musicRouted = false;
    }
}

export function initMusic(src) {
    if (musicEl) return musicEl;
    if (typeof Audio === 'undefined') return null;
    musicEl = new Audio(src);
    musicEl.loop = true;
    musicEl.preload = 'auto';
    try {
        const resolved = new URL(src, window.location.href);
        if (resolved.origin !== window.location.origin) musicEl.crossOrigin = 'anonymous';
    } catch (e) { musicEl.crossOrigin = null; }
    musicEl.volume = clamp01(musicVol * MUSIC_CEILING);
    musicEl.addEventListener('error', () => { musicWanted = false; });
    if (ctx) routeMusic();
    return musicEl;
}

export function startMusic() {
    if (!musicEl) return;
    musicWanted = true;
    if (musicVol <= 0) return;
    const audio = ensureCtx();
    if (audio && audio.state === 'suspended') audio.resume().catch(() => {});
    if (fadeRaf) { cancelAnimationFrame(fadeRaf); fadeRaf = null; }
    applyMusicVol(musicVol);
    playMusicEl();
}

export function stopMusic() {
    musicWanted = false;
    if (fadeRaf) { cancelAnimationFrame(fadeRaf); fadeRaf = null; }
    haltMusicEl();
    applyMusicVol(musicVol);
}

export function fadeMusicOut(durationMs) {
    if (!musicEl || musicEl.paused) return;
    const ms = durationMs || 1200;
    const start = performance.now();
    const from = musicVol;
    if (fadeRaf) cancelAnimationFrame(fadeRaf);
    const step = (now) => {
        const k = Math.min(1, (now - start) / ms);
        applyMusicVol(from * (1 - k));
        if (k < 1) {
            fadeRaf = requestAnimationFrame(step);
        } else {
            fadeRaf = null;
            musicWanted = false;
            haltMusicEl();
            applyMusicVol(musicVol);
        }
    };
    fadeRaf = requestAnimationFrame(step);
}

function applyMusicVol(v) {
    const level = Math.max(0, v) * MUSIC_CEILING;
    if (musicRouted && musicBus && ctx) {
        musicBus.gain.setTargetAtTime(level, ctx.currentTime, 0.01);
    } else if (musicEl) {
        musicEl.volume = clamp01(level);
    }
}

export function setMusicVolume(v) {
    musicVol = clamp01(Number(v) || 0);
    storeVol(MUSIC_KEY, musicVol);
    if (fadeRaf) { cancelAnimationFrame(fadeRaf); fadeRaf = null; }
    applyMusicVol(musicVol);
    if (!musicEl) return;
    if (musicVol <= 0) {
        musicEl.pause();
    } else if (musicWanted && musicEl.paused) {
        const audio = ensureCtx();
        if (audio && audio.state === 'suspended') audio.resume().catch(() => {});
        playMusicEl();
    }
}

export function setSfxVolume(v) {
    sfxVol = clamp01(Number(v) || 0);
    storeVol(SFX_KEY, sfxVol);
    if (sfxBus && ctx) sfxBus.gain.setTargetAtTime(sfxVol, ctx.currentTime, 0.01);
    if (sfxVol <= 0) stopReelLoop();
}

export function getMusicVolume() { return musicVol; }
export function getSfxVolume() { return sfxVol; }
export function isMobileAudio() { return IS_MOBILE; }

export function releaseAudio() {
    pending = [];
    stopReelLoop();
    stopMusic();
    if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
    if (ctx && ctx.state === 'running') ctx.suspend().catch(() => {});
}
